'use client';
import type { ICounter } from './types';

import React from 'react';
import { Minus, Plus, Trash2 } from 'react-feather';
import { useRouter } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { Button, Icon, InventoryModal, Text } from '@/components';
import { useBasketStore, useDisclosure } from '@/hooks';
import { routes } from '@/statics';
import { productCount } from '@/utils';
import { googleEventRemoveFromCart } from '@/utils/googleAnalyticsEvent';

const Counter: ICounter = ({ product }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const router = useRouter();

  const [count, decrease, increase, remove] = useBasketStore(
    useShallow((state) => [
      productCount({ products: state.products, code: product.code }),
      state.decrease,
      state.increase,
      state.remove,
    ])
  );

  const isExpanded = count > 0;

  return (
    <>
      <Button
        aria-label="counter"
        size="sm"
        isDisabled={
          !(product.inventory.is_available || product.type === 'view')
        }
        _disabled={{
          bg: 'gray.600',
          cursor: 'not-allowed',
          _hover: { bg: 'gray.600' },
        }}
        height={{ base: '30px', md: '46px' }}
        width={{
          base: isExpanded ? '94px' : '60px',
          md: isExpanded ? '132px' : '88px',
        }}
        transition="width 0.5s ease"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isExpanded) {
            if (product.type === 'view') {
              router.push(routes.product.href(product.code));
              return;
            }
            onOpen();
          }
        }}
        leftIcon={
          <Icon
            width="18px"
            height="18px"
            ml="2px"
            as={Plus}
            color="white"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              increase({ code: product.code });
            }}
            opacity={
              count < product.inventory.limit_buy_inventory ||
              product.type === 'view'
                ? 1
                : 0.3
            }
            display={isExpanded ? 'block' : 'none'}
          />
        }
        rightIcon={
          <Icon
            width={{ base: count > 1 ? '18px' : '14px', md: '18px' }}
            height={{ base: count > 1 ? '18px' : '14px', md: '18px' }}
            as={count > 1 ? Minus : Trash2}
            mr="2px"
            color="white"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (count === 1) {
                googleEventRemoveFromCart({
                  id: product.id,
                  title: product.title,
                  price: product.pricing?.price,
                  payable_price: product.pricing?.payable_price,
                  quantity: count,
                });
                remove({ code: product.code });
                return;
              }
              decrease({ code: product.code });
            }}
            opacity={isExpanded ? 1 : 0}
            display={isExpanded ? 'block' : 'none'}
          />
        }
      >
        {isExpanded ? (
          <Text
            fontWeight={700}
            fontSize="lg"
            height="18px"
            lineHeight="22px"
            color="white"
            minW={4}
            flex={1}
          >
            {count}
          </Text>
        ) : (
          <Text
            fontWeight={400}
            fontSize="2xl"
            color="white"
            lineHeight="22px"
            w="fit-content"
          >
            +
          </Text>
        )}
      </Button>
      {isOpen && (
        <InventoryModal onClose={onClose} isOpen={isOpen} product={product} />
      )}
    </>
  );
};
export default Counter;
